import React, { useEffect, useMemo } from 'react';
import { connect } from 'react-redux';
import { Field, formValueSelector, reduxForm } from 'redux-form';
import { MultiSelect } from '../../../../../../common/components';
import {
  CheckboxGroup,
  RadioGroup,
  SelectField,
} from '../../../../../../common/components/ReduxFormField';
import { get, debounce } from 'lodash';
import { FormGroup, Col, ControlLabel, Row } from 'react-bootstrap';
import flySureTravelCompanyNames from '../../../../../../helpers/flySureTravelCompanyNames';
import SingleDates from '../../../SingleDates/SingleDates';
import AnnualDates from '../../../AnnualDates/AnnualDates';
import './styles/TabContent.scss';
import Markdown from 'react-commonmark';


const DeclarationField = ({ content }) => {

  if (!content) {
    return null;
  }
  return (
    <>
      <div className="renewal-declaration">
        <Row>
          <Col sm={12}>
            <div className="renewal-declaration--title padding-title">{ 'Renewal Declaration'}</div>
          </Col>
        </Row>
        <Row>
          <Col sm={12}>
              <div>
                <div className="declaration">
                  <Markdown
                    source={content}
                  />
                </div>
              </div>
          </Col>
        </Row>
        <Row>
          <Col sm={12}>
            <div className="confirm-declaration">
              <Field
                name={'data.attributes.metadata.renewal_declaration.answer'}
                inline
                options={[
                  {
                    label: 'Please tick to confirm you agree to the above declaration',
                    value: true,
                  },
                ]}
                component={CheckboxGroup}
              />
            </div>
          </Col>
        </Row>
      </div>
    </>
  );

};

const mapToReactSelect = (options, isIntValue) => {
  return Object.keys(options).map((key) => {
    const name = options[key];
    return {
      label: name,
      value: isIntValue ? parseInt(key) : key,
    };
  });
};

const TripDetailsTab01 = ({
  onRegionDestinationChange,
  dispatch,
  change,
  currentProduct,
  formValues,
  renderDefinitions,
  isFlySure,
  handleSchemeTypeChange,
  isExpired,
  definitions,
  isMta,
}) => {

  useEffect(
    () => {
      dispatch(change('data.current_tab', 1));
    },
    [dispatch, change],
  );

  const handleDestinationChange = useMemo(
    () =>
      debounce(
        (values) =>
          onRegionDestinationChange(values, 'data.attributes.metadata.destinations'),
        1000,
      ),
    [onRegionDestinationChange],
  );

  const handleRegionChange = useMemo(
    () =>
      debounce((option) => {
        onRegionDestinationChange(option.value, 'data.attributes.metadata.region');
      }, 1000),
    [onRegionDestinationChange],
  );

  const minDate = formValues?.data?.attributes?.metadata.start_date;
  const tripType = formValues?.data?.attributes?.metadata?.scheme_type;
  const destinations = formValues?.data?.attributes?.metadata?.destinations;
  const regions = mapToReactSelect(currentProduct.data.attributes.metadata.regions || {});

  const annualTripDuration = mapToReactSelect(
    get(currentProduct, 'data.attributes.metadata.annual_trip_durations', {}),
    true,
  );

  const advanceDays = 608;

  return (
    <div className={'trip-details-section'}>

      <DeclarationField
        content={formValues?.data?.attributes?.metadata?.renewal_declaration?.declaration}

      />
      {!isFlySure ? (
        <FormGroup>
          <Col sm={4}>
            <ControlLabel>
              Which type of policy would you like? {renderDefinitions('trip_type')}
            </ControlLabel>
          </Col>
          <Col sm={8}>
            <Field
              name="data.attributes.metadata.scheme_type"
              onChange={handleSchemeTypeChange}
              component={RadioGroup}
              inline
              options={[
                { label: 'Single Trip', value: 'single' },
                { label: 'Annual Multi-trip', value: 'annual' },
              ]}
              isDisabled={isMta}
            />
          </Col>
        </FormGroup>
      ) : (
        <FormGroup>
          <Col sm={4}>
            <ControlLabel>Airline or Travel Industry Company Name</ControlLabel>
          </Col>
          <Col sm={8}>
            <Field
              name="data.attributes.metadata.travel_company_name"
              component={SelectField}
              options={flySureTravelCompanyNames().map((name) => ({
                value: name,
                label: name,
              }))}
            />
          </Col>
        </FormGroup>
      )}
      <FormGroup>
        <Col sm={4}>
          <ControlLabel>
            Are you going on a cruise? {renderDefinitions('cruise_cover')}
          </ControlLabel>
        </Col>
        <Col sm={8}>
          <Field
            name="data.attributes.metadata.options.cruise_cover"
            component={RadioGroup}
            inline
            options={[{ label: 'Yes', value: true }, { label: 'No', value: false }]}
          />
        </Col>
      </FormGroup>
      <SingleDates
        show={tripType !== 'annual'}
        dateDefinition={definitions?.travel_dates.single}
        tripTypeDefinition={definitions?.trip_type}
        isExpired={isExpired}
        advanceDays={advanceDays}
        minDate={minDate}
        maxDays={364}
        startDate={minDate}
      />
      <AnnualDates
        show={tripType === 'annual'}
        dateDefinition={definitions?.travel_dates?.annual}
        isExpired={isExpired}
        isMta={isMta}
      />

      {tripType !== 'annual' && (
        <FormGroup>
          <Col sm={4}>
            <ControlLabel>What countries are you travelling to?</ControlLabel>
          </Col>
          <Col sm={8}>
            <Field
              name="data.attributes.metadata.destinations"
              placeholder="Search for countries..."
              component={MultiSelect}
              source={'products/destinations'}
              sourceKey={'MultiCountries'}
              isClearable={true}
              optionValuePath={'attributes.name'}
              handleOnChange={handleDestinationChange}
            />
            {!destinations || destinations.length === 0 ? (
              <span className="multiple-countries-text">
                NOTE: Multiple countries can be added
              </span>
            ) : null}
          </Col>
        </FormGroup>
      )}

      {tripType === 'annual' && (
        <FormGroup>
          <Col sm={4}>
            <ControlLabel>What region are you going to?</ControlLabel>
          </Col>
          <Col sm={4}>
            <Field
              name="data.attributes.metadata.region"
              infoButton={renderDefinitions('region')}
              noFormGroup
              component={SelectField}
              options={regions}
              handleOnChange={handleRegionChange}
            />
          </Col>
        </FormGroup>
      )}
      {tripType === 'annual' && (
        <FormGroup>
          <Col sm={4}>
            <ControlLabel>What is the duration of the longest trip?</ControlLabel>
          </Col>
          <Col sm={4}>
            <Field
              name="data.attributes.metadata.max_trip_duration"
              infoButton={renderDefinitions('region')}
              mdColumnSize={4}
              noFormGroup
              component={SelectField}
              options={annualTripDuration}
            />
          </Col>
        </FormGroup>
      )}
      <div />
    </div>
  );
};

const FORM_NAME = 'LeisureTravelForm';
const form = reduxForm({ form: FORM_NAME })(TripDetailsTab01);
const selector = formValueSelector(FORM_NAME);
const mapStateToProps = (state, props) => {
  const values = selector(state, 'data.id', 'data.attributes.metadata');

  return {
    initialValues: props.resource,
    form: 'LeisureTravelForm',
    formValues: values,
    destroyOnUnmount: false,
    forceUnregisterOnUnmount: true,
  };
};

export default connect(mapStateToProps)(form);
